'use strict';

/**
 * Support関連の共通処理モジュール
 * support.js と support_myaccount.js の共通機能を提供
 */

// 共通設定定数
const SUPPORT_CONFIG = {
    FORM_TYPES: {
        MY_ORDER: 'myOrder',
        AFTER_SERVICE: 'afterService', 
        GENERAL_QUESTION: 'generalQuestion',
        WHOLESALE: 'wholesale',
        COLLABORATION_REQUEST: 'collaborationRequest'
    },
    TAB_NAMES: {
        'myOrder': 'My Order',
        'afterService': 'After Service',
        'generalQuestion': 'General Question',
        'wholesale': 'Distributors & Retailers',
        'collaborationRequest': 'Collaboration Request'
    }
};

/**
 * ファイル添付が必要なフォーム判定
 * @param {string} formClass - フォームのクラス名
 * @returns {boolean} ファイル添付が必要かどうか
 */
function requiresFileAttachment(formClass) {
    return formClass === SUPPORT_CONFIG.FORM_TYPES.AFTER_SERVICE || 
           formClass === SUPPORT_CONFIG.FORM_TYPES.MY_ORDER;
}

/**
 * ファイル添付が必須のフォーム判定
 * @param {string} formClass - フォームのクラス名
 * @returns {boolean} ファイル添付が必須かどうか
 */
function isFileAttachmentRequired(formClass) {
    return formClass === SUPPORT_CONFIG.FORM_TYPES.AFTER_SERVICE;
}

/**
 * 改善されたファイル添付処理
 * @param {FormData} formData - フォームデータ
 * @param {jQuery} form - jQueryフォーム要素
 * @param {Object} fileAttachment - fileAttachmentモジュール
 * @returns {Object} {success: boolean, fileCount: number, errors: Array}
 */
function processFileAttachment(formData, form, fileAttachment) {
    const result = { success: true, fileCount: 0, errors: [] };
    
    try {
        const formClass = form.attr('class');
        
        // ファイル添付が不要なフォームは早期リターン
        if (!requiresFileAttachment(formClass)) {
            return result;
        }

        const files = form.find('.jsAttacheFile').data('files');
        let sendFileNames = [];
        
        if (files) {
            sendFileNames = files.split(',').filter(name => name.trim());
        }
        
        formData.append('fileNumber', sendFileNames.length);
        result.fileCount = sendFileNames.length;
        
        // 蓄積されたファイルを使用
        const formId = formClass || form.attr('id') || 'default';
        const fileDataArray = fileAttachment.getAccumulatedFiles(formId);
        
        // パフォーマンス最適化: Mapを使用して高速検索
        const fileMap = new Map();
        for (const file of fileDataArray) {
            fileMap.set(file.name, file);
        }
        
        // ファイルの添付処理
        let attachedCount = 0;
        for (let i = 0; i < sendFileNames.length; i++) {
            const fileName = sendFileNames[i];
            const file = fileMap.get(fileName);
            
            if (file) {
                formData.append('file' + i, file);
                attachedCount++;
            } else {
                result.errors.push(`File not found: ${fileName}`);
                console.warn(`❌ File not found in accumulated files: ${fileName}`);
            }
        }
        
        // エラーチェック
        if (attachedCount !== sendFileNames.length) {
            result.success = false;
            console.error(`❌ File attachment mismatch. Expected: ${sendFileNames.length}, Attached: ${attachedCount}`);
        }
        
        console.log(`📎 File attachment complete. Files: ${attachedCount}/${sendFileNames.length}`);
        
    } catch (error) {
        result.success = false;
        result.errors.push(`File attachment error: ${error.message}`);
        console.error('❌ File attachment processing failed:', error);
    }
    
    return result;
}

/**
 * ファイル添付バリデーション
 * @param {jQuery} form - jQueryフォーム要素  
 * @returns {Object} {isValid: boolean, errorMessage: string}
 */
function validateFileAttachment(form) {
    const formClass = form.attr('class');
    
    // ファイル添付が必須でないフォームは常に有効
    if (!isFileAttachmentRequired(formClass)) {
        return { isValid: true, errorMessage: null };
    }
    
    const hasFiles = form.find('.jsAttacheFile').data('files');
    
    if (!hasFiles) {
        const fileInput = form.find('.jsAttacheFile');
        return { 
            isValid: false, 
            errorMessage: fileInput.data('missing-error') || 'File attachment is required.'
        };
    }
    
    return { isValid: true, errorMessage: null };
}

/**
 * フォームリセット処理の統一
 * @param {string} activeTabName - アクティブなタブ名
 * @param {Object} fileAttachment - fileAttachmentモジュール
 * @param {Object} clientSideValidation - clientSideValidationモジュール
 */
function resetFormsExcept(activeTabName, fileAttachment, clientSideValidation) {
    const allFormTypes = Object.values(SUPPORT_CONFIG.FORM_TYPES);
    const formsToReset = allFormTypes.filter(formName => formName !== activeTabName);
    
    for (const formName of formsToReset) {
        const form = $('form.' + formName);
        
        if (form.length) {
            // フォームリセット
            form.get(0).reset();
            clientSideValidation.functions.clearForm(form.get(0));
            
            // ファイル添付のクリア
            if (requiresFileAttachment(formName)) {
                fileAttachment.clearFileName(form);
            }
        }
    }
}

module.exports = {
    SUPPORT_CONFIG,
    requiresFileAttachment,
    isFileAttachmentRequired,
    processFileAttachment,
    validateFileAttachment,
    resetFormsExcept
};
